import React, {
  useEffect, useMemo, useReducer, useState,
} from 'react';
import {
  Form, Slider,
  Col, Row, Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Buckets } from 'ontraccr-common';
import { withScriptjs } from 'react-google-maps';

import '@ant-design/compatible/assets/index.css';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import OnTraccrNumberInput from '../common/inputs/OnTraccrNumberInput';
import FormTextInput from '../common/inputs/FormTextInput';
import OnTraccrMap from '../common/map/OnTraccrMap';
import DisplayText from '../common/text/DisplayText';
import IntegrationTag from '../common/IntegrationTag';
import DivisionSelector from '../common/inputs/DivisionSelector';
import getGoogleMapKey from '../common/keys';
import ContactSelector from '../common/inputs/ContactSelector';
import ProjectApproverSelector from '../common/inputs/ProjectApproverSelector';
import LabelSelector from '../labels/LabelSelector';
import ProjectLabels from './ProjectLabels';

import config from '../config';
import { getIdMap, safeExtractFromObject } from '../helpers/helpers';
import { getQuickbooksClasses } from '../settings/state/settings.actions';
import {
  geocodeProject,
  getProjectCostUpdates,
  getProjectCustomData,
  getProjectCustomFieldTemplate,
} from './state/projects.actions';
import { getBucketSelect } from '../buckets/bucketHelpers';
import CustomFields from '../common/customFields/CustomFields';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';

const { Option } = Select;

const getGeofence = (props) => {
  const {
    geofence,
    geofenceEnabled,
    geofenceSize,
    settings,
  } = props;
  if (!settings.enableGeofence && !settings.enableClockOutGeofence) return null;
  if (geofenceEnabled) return geofenceSize * 1000;
  if (geofence) return geofence;
  return null;
};

const validateKey = (t, id, projects, key) => (rule, value, callback) => {
  if (projects.some((project) => project[key] === value && id !== project.id)) {
    return callback(`${t('Project')} ${key} ${value} is in use by another ${t('Project').toLowerCase()}`);
  }

  callback();
};

function ProjectAddGeneral({
  marker,
  onPlaceChanged,
  geofenceEnabled,
  onGeofenceEnableChanged,
  onGeofenceSizeChanged,
  geofenceSize,
  isNotDisplay,
  id,
  name,
  number,
  customer,
  customerId,
  info,
  city,
  geofence,
  latitude,
  longitude,
  address,
  settings = {},
  firstApprover,
  secondApprover,
  projectTypeId,
  qboClassId,
  onApproverChanged1,
  onApproverChanged2,
  onProjectTypeChanged,
  projects = [],
  intuitId,
  procoreId,
  sageId,
  eclipseId,
  formRef,
  projectTypes,
  type,
  divisionId,
  hideBucketDropdown,
  errors,
  customFields,
  setCustomFields,
  useCoordinates,
  onLabelsChanged,
  onUseCoordinatesChanged,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    connectedToQuickbooks = false,
  } = useSelector((state) => state.settings.company);
  const { enableTimecardSubmission = false } = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const buckets = useSelector((state) => state.buckets.buckets);
  const {
    customData,
    customDataFiles,
    customFieldTemplate,
    quickbookClassMap: qboClassMap,
  } = useSelector((state) => state.projects);

  const [selectedDivisionId, setSelectedDivisionId] = useState(divisionId);
  const [shouldUpdate, forceUpdate] = useReducer((x) => x + 1, 0);
  const [stateLatitude, setStateLatitude] = useState(latitude);
  const [stateLongitude, setStateLongitude] = useState(longitude);
  const [stateAddress, setStateAddress] = useState(address);
  const [selectedProject, setSelectedProject] = useState({ customerId, projectId: id });

  useEffect(() => {
    if (!connectedToQuickbooks) return;
    dispatch(getQuickbooksClasses());
  }, [connectedToQuickbooks]);

  useEffect(() => {
    dispatch(getProjectCustomFieldTemplate());
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(getProjectCostUpdates(id));
    }

    dispatch(getProjectCustomData(id));
  }, [id]);

  useEffect(() => {
    setCustomFields(customFieldTemplate?.sections);
  }, [customFieldTemplate]);

  useEffect(() => {
    forceUpdate();
  }, [customData]);

  const relevantQBOClasses = qboClassMap[selectedDivisionId];
  const selectedQBOClass = useMemo(() => (
    qboClassMap[divisionId]?.find(({ Id }) => Id === qboClassId)
  ), [qboClassMap, qboClassId]);
  const projectMap = getIdMap(projects);
  const bucketMap = getIdMap(buckets);

  const {
    upstreamBucketIds,
  } = useMemo(() => (
    Buckets.getLinkedBuckets(buckets, [id], ['projects'])
  ), [id, buckets]);

  const projectTypeMap = getIdMap(projectTypes);
  const projectText = t('Project');
  const projectLower = projectText.toLowerCase();

  const onUseCoordinatesToggled = (checked) => {
    onUseCoordinatesChanged(checked);
    if (checked) {
      const newAddress = (stateLatitude && stateLongitude) ? `${stateLatitude}, ${stateLongitude}` : null;
      setStateAddress(newAddress);
      formRef?.current?.setFieldsValue({ address: newAddress });
    }
  };

  const onDivisionChanged = (value) => {
    setSelectedDivisionId(value);
    formRef?.current?.setFieldsValue({ qboClassId: null });
  };

  const onPlaceChangeTriggered = (place) => {
    const { lat, lng, address: placeAddress } = place;
    setStateLatitude(lat);
    setStateLongitude(lng);
    setStateAddress(placeAddress);
    onPlaceChanged(place);
  };

  const onMarkerDrag = ({
    lat,
    lng,
  }) => {
    setStateLatitude(lat);
    setStateLongitude(lng);
    const newAddress = `${lat}, ${lng}`;
    setStateAddress(newAddress);
    onPlaceChanged({
      lat,
      lng,
      address: newAddress,
    });
    onUseCoordinatesChanged(true);
  };

  const { BucketSelect, options: bucketOptions = [] } = useMemo(() => (
    getBucketSelect({
      selectedBuckets: upstreamBucketIds,
      buckets,
      bucketMap,
      projectMap,
      type: 'projects',
      getDownstream: true,
      isDisplay: !isNotDisplay,
    })
  ), [
    upstreamBucketIds,
    buckets,
    projectMap,
    isNotDisplay,
  ]);

  useEffect(() => {
    const shouldGeocode = id
      && address
      && longitude === 0
      && latitude === 0
      && intuitId;

    if (shouldGeocode) {
      const getGeocodeProject = async () => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({
          address,
        }, async (results, status) => {
          if (status === 'OK' && results[0]) {
            const { location } = results[0].geometry || {};
            const lat = location?.lat();
            const lng = location?.lng();
            const coords = await dispatch(geocodeProject(id, { lat, lng }));
            if (coords) {
              setStateLatitude(coords.lat);
              setStateLongitude(coords.lng);
            }
          } else {
            // silent fail
          }
        });
      };
      getGeocodeProject();
    }
  }, [
    id,
    address,
    longitude,
    latitude,
    intuitId,
  ]);

  const customFieldDivisions = useMemo(() => [selectedDivisionId], [selectedDivisionId]);

  const scrollOffset = isNotDisplay ? 190 : 165;
  return (
    <div style={{ height: `calc(100vh - ${scrollOffset}px`, overflowY: 'auto' }}>
      <Row style={{ width: '100%' }} gutter={20}>
        <Col span={14}>
          <Row gutter={20}>
            <Col span={8}>
              <Form.Item
                name="name"
                label={`${projectText} Name`}
                style={{ marginBottom: 0 }}
                rules={[
                  { required: isNotDisplay, message: `Please enter the ${projectLower} name` },
                ]}
              >
                {isNotDisplay ? (
                  <OnTraccrTextInput
                    style={{ width: '100%' }}
                  />
                ) : <DisplayText title={name} /> }
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="number"
                label={`${projectText} Number`}
                style={{ marginBottom: 0 }}
                rules={[
                  { validator: validateKey(t, id, projects, 'number') },
                ]}
              >
                {isNotDisplay ? (
                  <OnTraccrTextInput
                    style={{ width: '100%' }}
                  />
                ) : <DisplayText title={number} /> }
              </Form.Item>
            </Col>
            <Col span={8}>
              {config.showDivisions ? (
                <Form.Item
                  name="divisionId"
                  key="divisionId"
                  label="Division"
                  style={{ marginBottom: 0 }}
                  rules={[{ required: isNotDisplay, message: 'Select a division' }]}
                  valuePropName="divisionId"
                >
                  <DivisionSelector
                    displayMode={!isNotDisplay}
                    onChange={onDivisionChanged}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="customer"
                  label="Customer"
                  style={{ marginBottom: 0 }}
                >
                  {isNotDisplay
                    ? <Select />
                    : <DisplayText title={customer} /> }
                </Form.Item>
              )}
            </Col>
          </Row>
          <Row gutter={20} style={{ marginTop: 30 }}>
            <Col span={12}>
              <Form.Item
                name="labels"
                label="Labels"
                style={{ marginBottom: 0 }}
              >
                {isNotDisplay ? (
                  <LabelSelector
                    type="projects"
                    onChange={onLabelsChanged}
                    isDisplay={false}
                  />
                ) : (
                  <ProjectLabels projectId={id} size="default" />
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="city"
                label="City"
                style={{ marginBottom: 0 }}
              >
                {isNotDisplay ? (
                  <OnTraccrTextInput
                    style={{ width: '100%' }}
                  />
                ) : <DisplayText title={city} />}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={20} style={{ marginTop: 30, height: 62 }}>
            <Col span={8} hidden={!enableTimecardSubmission}>
              <Form.Item
                name="firstApprover"
                label="First Approver"
                valuePropName="selectedApprover"
                style={{ marginBottom: 0 }}
              >
                <ProjectApproverSelector
                  onChange={onApproverChanged1}
                  isNotDisplay={isNotDisplay}
                  selectedApprover={firstApprover}
                  isFirst
                />
              </Form.Item>
            </Col>
            <Col span={8} hidden={!enableTimecardSubmission}>
              <Form.Item
                name="secondApprover"
                label="Second Approver"
                valuePropName="selectedApprover"
                style={{ marginBottom: 0 }}
              >
                <ProjectApproverSelector
                  onChange={onApproverChanged2}
                  isNotDisplay={isNotDisplay}
                  selectedApprover={secondApprover}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              {config.showDivisions && (
                <ContactSelector
                  isNotDisplay={isNotDisplay}
                  contact={customer}
                  formRef={formRef}
                  divisionId={selectedDivisionId}
                  shouldFilterByDivision
                  setCustomerId={(newCustomerId) => {
                    setSelectedProject({ ...selectedProject, customerId: newCustomerId });
                  }}
                />
              )}
            </Col>
          </Row>
          <Row
            style={{ height: isNotDisplay ? 90 : 36, marginTop: 30 }}
            align="middle"
            gutter={20}
          >
            <Col span={8} style={{ height: '100%' }}>
              <Form.Item
                name="projectTypeId"
                label="Type"
                style={{ marginBottom: 0 }}
              >
                { isNotDisplay ? (
                  <Select
                    showSearch
                    onChange={onProjectTypeChanged}
                    disabled={!!type}
                  >
                    <Option key="-1" value={null}>None</Option>
                    {projectTypes.map((type) => (
                      <Option key={type.id} value={type.id}>
                        {type.name}
                      </Option>
                    ))}
                  </Select>
                ) : <DisplayText title={projectTypeMap[projectTypeId]?.name ?? 'None'} />}
              </Form.Item>
            </Col>
            <Col span={isNotDisplay ? 6 : 8} style={{ height: '100%' }}>
              {(settings.enableGeofence || settings.enableClockOutGeofence) && (
                <Row style={{ height: '100%', width: '100%' }} align="middle">
                  {isNotDisplay ? (
                    <OnTraccrCheckbox
                      onChange={onGeofenceEnableChanged}
                      value={geofenceEnabled}
                      style={{ marginBottom: 0 }}
                      label="Enable Geofencing?"
                    />
                  ) : (
                    <Form.Item
                      name="geofence"
                      label="Geofence"
                      style={{ marginBottom: 0 }}
                    >
                      <DisplayText title={geofence ? `${(geofence / 1000)} km` : 'Disabled'} />
                    </Form.Item>
                  )}
                </Row>
              )}
            </Col>
            {isNotDisplay && (
              <Col span={6} style={{ height: '100%' }}>
                <Row style={{ height: '100%', width: '100%' }} align="middle">
                  <OnTraccrCheckbox
                    onChange={onUseCoordinatesToggled}
                    value={useCoordinates}
                    style={{ marginBottom: 0 }}
                    label="Use Coordinates?"
                  />
                </Row>
              </Col>
            )}
            {!isNotDisplay && (
              <Col style={{ height: '100%' }} span={8}>
                <Form.Item
                  name="integrations"
                  key="integrations"
                  label="Integrations"
                  style={{ marginBottom: 0 }}
                >
                  <IntegrationTag id={intuitId} title="QuickBooks" />
                  <IntegrationTag id={procoreId} title="Procore" />
                  <IntegrationTag id={sageId} title="Sage" />
                  <IntegrationTag id={eclipseId} title="Eclipse" />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Row>
            <Col span={18}>
              <Row
                style={{
                  height: '100%',
                  width: '100%',
                  flexDirection: 'row-reverse',
                  margin: 0,
                }}
                gutter={16}
                justify="start"
                align="middle"
              >
                <Col flex="80px" style={{ padding: 0 }}>
                  <Row style={{ height: '100%', width: '100%' }} align="middle">
                    {geofenceEnabled && isNotDisplay && (
                      <OnTraccrNumberInput
                        style={{ width: '100%' }}
                        min={0.05}
                        max={5.0}
                        step={0.05}
                        onChange={onGeofenceSizeChanged}
                        value={geofenceSize}
                        formatter={(value) => `${value} km`}
                        parser={(value) => value.replace(/[k,m ]/g, '')}
                        defaultValue={3}
                      />
                    )}
                  </Row>
                </Col>
                <Col flex="auto">
                  {geofenceEnabled && isNotDisplay && (
                    <Slider
                      min={0.05}
                      max={5.0}
                      step={0.05}
                      tooltipVisible={false}
                      onChange={onGeofenceSizeChanged}
                      value={geofenceSize}
                    />
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={10} style={{ height: 250 }}>
          {isNotDisplay && (
            <Row>
              <span style={{ color: 'red', marginRight: '5px' }}>*</span>
              Project Location
            </Row>
          )}
          <Row>
            <OnTraccrMap
              showSearch={isNotDisplay}
              lat={stateLatitude}
              lng={stateLongitude}
              address={stateAddress}
              mapsQuery={stateAddress}
              marker={marker}
              isNotDisplay={isNotDisplay}
              googleMapURL={getGoogleMapKey()}
              loadingElement={<div style={{ height: '100%' }} />}
              containerElement={(
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    height: 250,
                    top: isNotDisplay ? 30 : 0,
                  }}
                />
              )}
              mapElement={<div style={{ height: '100%' }} />}
              onPlaceChanged={onPlaceChangeTriggered}
              geofence={getGeofence({
                settings,
                geofence,
                geofenceEnabled,
                geofenceSize,
              })}
              useCoordinates={useCoordinates}
              showPin={!!stateLatitude && !!stateLongitude}
              isProject
              markersAreDraggable={isNotDisplay}
              onMarkerDrag={onMarkerDrag}
            />
            <Form.Item
              name="address"
              rules={[{ required: isNotDisplay, message: useCoordinates ? 'Please enter valid coordinates' : 'Please enter an address' }]}
              style={{
                position: 'relative',
                top: 230,
                left: 0,
                pointerEvents: 'none',
              }}
            >
              <div hidden />
            </Form.Item>
          </Row>
        </Col>
      </Row>

      <Row gutter={20} style={{ marginTop: 30 }}>
        <Col span={8}>
          <FormTextInput
            isNotDisplay={isNotDisplay}
            value={info}
            name="info"
            label="Info"
            textarea
            autoSize
            supportMultiLine
          />
        </Col>
        { !hideBucketDropdown && bucketOptions?.length && (
          <Col span={8} style={{ paddingTop: 10 }}>
            <Form.Item
              name="upstreamBucketIds"
              key="upstreamBucketIds"
              label="Buckets"
              style={{
                width: '100%',
                marginBottom: 0,
              }}
            >
              {BucketSelect}
            </Form.Item>
          </Col>
        )}
        { !!relevantQBOClasses && (
          <Col span={8}>
            <Form.Item
              name="qboClassId"
              label="QuickBooks Class"
              style={{ marginBottom: 0 }}
            >
              {isNotDisplay ? (
                <Select
                  showSearch
                  optionFilterProp="label"
                >
                  <Option key="-1" value={null}>None</Option>
                  { relevantQBOClasses?.map(({ Id, Name }) => (
                    <Option key={Id} value={Id} label={Name}>
                      {Name}
                    </Option>
                  ))}
                </Select>
              ) : <DisplayText title={selectedQBOClass?.Name || 'Not Set'} /> }
            </Form.Item>
          </Col>
        )}
      </Row>

      {/* Hidden fields for map info */}
      {isNotDisplay && (
        <>
          <Form.Item name="lat">
            <div hidden />
          </Form.Item>
          <Form.Item name="lng">
            <div hidden />
          </Form.Item>
        </>
      )}

      { !!customFields?.length && (
        <Row>
          <Form.Item
            name="customData"
            style={{ marginBottom: 20, width: '100%' }}
          >
            <CustomFields
              key={`${id}${shouldUpdate}`} // Need to force re-render
              initialValues={customData}
              customFields={customFields}
              divisions={customFieldDivisions}
              selectedProject={selectedProject}
              errors={errors}
              fileMap={customDataFiles}
              isDisplay={!isNotDisplay}
            />
          </Form.Item>
        </Row>
      )}
    </div>
  );
}

export default withScriptjs(ProjectAddGeneral);
